import type { Account, Card, Money } from './types'

/*
 * Formatting, in the customer's own locale.
 *
 * The APIs send RFC 3339 instants and never formatted dates — a pre-formatted string cannot be
 * re-localised, and the Website's `ToBjLongDateString()` bakes one server's culture into the response.
 * `Intl` is the whole implementation.
 */

const longDate = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
})

/**
 * UTC, deliberately. These are dates rather than moments — a subscription renews ON a day — so rendering
 * `2027-01-14T00:00:00Z` in a negative offset would show the 13th to somebody in New York.
 */
export const formatDate = (iso: string | null | undefined): string =>
  iso ? longDate.format(new Date(iso)) : ''

/**
 * An amount in the customer's own locale, in the currency the API says they are billed in.
 *
 * The two halves are deliberately owned in different places: the API chooses the CURRENCY, because that
 * is a billing decision; the browser chooses the FORMAT, because that is a locale decision. An API that
 * sent a rendered string would be making both, and the string could not then be re-localised.
 *
 * THE EXPONENT IS ASKED FOR, NOT ASSUMED. Amounts are in minor units and `/100` is wrong for currencies
 * that have no minor unit at all — ¥1000 would render as ¥10. `resolvedOptions()` reports the right
 * number of fraction digits for whatever currency is passed, so this is correct without a table.
 */
export function formatMoney(money: Money): string {
  const format = new Intl.NumberFormat(undefined, { style: 'currency', currency: money.currency })
  const exponent = format.resolvedOptions().maximumFractionDigits ?? 2
  return format.format(money.amount / 10 ** exponent)
}

/** "11 / 2027", matching how an expiry is printed on the card itself. */
export const formatCardExpiry = (card: Card): string =>
  `${String(card.expiryMonth).padStart(2, '0')} / ${card.expiryYear}`

/**
 * The moment a card stops working: the first instant of the month AFTER its expiry month. A card marked
 * 11/2027 is valid throughout November.
 */
export const cardValidUntil = (card: Card): Date => new Date(Date.UTC(card.expiryYear, card.expiryMonth, 1))

export const cardExpired = (card: Card): boolean => cardValidUntil(card) <= new Date()

export const cardExpiresBefore = (card: Card, iso: string | null): boolean =>
  iso !== null && cardValidUntil(card) < new Date(iso)

/**
 * What the Summary page calls the account. Matches AccountSummaryViewModel.AccountType exactly, including
 * the parenthetical for a trial — it is the phrase support conversations already use.
 */
export const accountTypeLabel = (account: Account): string => {
  switch (account.premium.state) {
    case 'trial':
      return 'Free (Premium Trial)'
    case 'premium':
      return 'Premium'
    default:
      return 'Free'
  }
}

export const isPremium = (account: Account): boolean => account.premium.state !== 'free'

/** The referral link's shape belongs to the marketing site, so it is composed here from its base URL. */
export const referralUrl = (marketingBase: string, code: string): string =>
  `${marketingBase}/introduce/${code}`

export const plural = (count: number, singular: string, pluralForm = `${singular}s`): string =>
  count === 1 ? singular : pluralForm
